import {
  AppstoreOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  FlagOutlined,
  HeartOutlined,
  StopOutlined,
  TrophyOutlined
} from "@ant-design/icons"
import { Flex } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../../utils/cn.ts"
import {
  LEAD_STATUSES,
  leadStatusColorMap
} from "../../../../Lead/constants/lead-statuses.ts"
import {
  INACTIVE_REASONS,
  REASON_STATUSES,
  WON_REASON_KEY,
  type InactiveReasonKey
} from "../../../../Lead/constants/operational-states.ts"
import {
  clearedInactiveFilters,
  narrowsInactiveFilters
} from "../../../redux/leads.constants.ts"
import type { LeadsFilters } from "../../../redux/leads.types.ts"
import type { InactiveCounts } from "../../../api/services.types.ts"

const CHIP_ICONS: Partial<Record<InactiveReasonKey, React.ReactNode>> = {
  rehash_nurture: <HeartOutlined />,
  lost: <FlagOutlined />,
  unqualified: <CloseCircleOutlined />,
  do_not_contact: <StopOutlined />,
  [WON_REASON_KEY]: <TrophyOutlined />
}

// Read from the same tokens the table's own status tags render, through the
// shared reason/status pairing, so a chip cannot drift from the rows it
// filters to.
const CHIP_TINTS: Partial<Record<InactiveReasonKey, string>> = {
  ...Object.fromEntries(
    Object.entries(REASON_STATUSES).map(([reason, status]) => [
      reason,
      leadStatusColorMap[status].color
    ])
  ),
  [WON_REASON_KEY]: leadStatusColorMap[LEAD_STATUSES.WON].color
}

interface Chip {
  key: "all" | InactiveReasonKey
  label: string
  icon: React.ReactNode
  tint?: string
  count?: number
}

interface InactiveSmartFiltersProps {
  filters: LeadsFilters
  counts?: InactiveCounts | null
  /** Won/Converted is gated the same way it always was — behind lead_view_archive. */
  canSeeWon?: boolean
  disabled?: boolean
  onFiltersChange: (filters: Partial<LeadsFilters>) => void
}

export function InactiveSmartFilters({
  filters,
  counts,
  canSeeWon,
  disabled,
  onFiltersChange
}: InactiveSmartFiltersProps) {
  const { t } = useTranslation()
  const countByState = new Map(
    (counts?.queues || []).map((row) => [row.state, row.count])
  )

  const chips: Chip[] = [
    {
      key: "all",
      label: t("Inactive Leads"),
      icon: <AppstoreOutlined />,
      tint: "#8B5CF6",
      count: counts?.total
    },
    ...INACTIVE_REASONS.filter(
      (reason) => reason.state !== WON_REASON_KEY || canSeeWon
    ).map((reason) => ({
      key: reason.state,
      label: t(reason.label),
      icon: CHIP_ICONS[reason.state],
      tint: CHIP_TINTS[reason.state],
      count: countByState.get(reason.state) ?? 0
    }))
  ]

  const isActive = (key: Chip["key"]) =>
    key === "all"
      ? !filters?.reason?.length
      : filters?.reason?.length === 1 && filters.reason[0] === key

  const selectChip = (key: Chip["key"]) => {
    onFiltersChange(
      key === "all"
        ? clearedInactiveFilters()
        : { ...clearedInactiveFilters(), reason: [key as InactiveReasonKey] }
    )
  }

  return (
    <Flex wrap="wrap" gap="small" align="center" className={"px-4 py-3"}>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          data-testid={`inactive-smart-chip-${chip.key}`}
          disabled={disabled}
          aria-pressed={isActive(chip.key)}
          onClick={() => selectChip(chip.key)}
          className={cn(
            "inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border bg-white px-3.5 text-[13px] font-medium transition-colors",
            "disabled:cursor-not-allowed disabled:opacity-60",
            isActive(chip.key)
              ? "border-blue-500 bg-blue-50 text-blue-600"
              : "border-slate-200 text-slate-700 hover:border-slate-300"
          )}
        >
          <span className={"leading-none"} style={{ color: chip.tint }}>
            {chip.icon}
          </span>
          {chip.label}
          {chip.count !== undefined && (
            // Tinted to the chip's own colour, same as the Active page's
            // Smart Filter row, rather than boxed in a neutral badge.
            <span className={"font-semibold"} style={{ color: chip.tint }}>
              {chip.count}
            </span>
          )}
        </button>
      ))}
      {narrowsInactiveFilters(filters) && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onFiltersChange(clearedInactiveFilters())}
          className={cn(
            "inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-slate-300 bg-white px-3.5 text-[13px] font-medium text-slate-600 transition-colors",
            "hover:border-slate-400 hover:text-slate-900",
            "disabled:cursor-not-allowed disabled:opacity-60"
          )}
        >
          <CloseOutlined />
          {t("Clear Filters")}
        </button>
      )}
    </Flex>
  )
}
